import { addMinutes, format } from 'date-fns';
import * as Yup from 'yup';
import { parseOrgDateOnly } from '@/shared/utils/timezone';
import type {
  AppointmentRecord,
  CreateAppointmentPayload,
  UpdateAppointmentPayload,
} from '../api/appointment.api';
import { getAppointmentTypeId, MIN_APPOINTMENT_DURATION } from './appointmentTypes';
import { getChairId } from './dentalChairs';
import { isTerminalStatus } from './appointmentStatus';

export type AppointmentFormMode = 'create' | 'edit';

export type AppointmentSubjectType = 'patient' | 'lead';

export const APPOINTMENT_VISIT_KINDS = ['new', 'follow_up'] as const;

export type AppointmentVisitKind = (typeof APPOINTMENT_VISIT_KINDS)[number];

export const isAppointmentVisitKind = (value: unknown): value is AppointmentVisitKind =>
  value === 'new' || value === 'follow_up';

export interface AppointmentFormValues {
  subjectType: AppointmentSubjectType;
  patientId: string;
  leadId: string;
  doctorId: string;
  clinicId: string;
  chairId: string;
  date: Date | null;
  startTime: string;
  duration: number | null;
  type: string;
  visitKind: AppointmentVisitKind | '';
  status: string;
  reasonForVisit: string;
  notes: string;
  treatmentId: string;
  cancelReason: string;
}

export const EMPTY_APPOINTMENT_FORM_VALUES: AppointmentFormValues = {
  subjectType: 'patient',
  patientId: '',
  leadId: '',
  doctorId: '',
  clinicId: '',
  chairId: '',
  date: null,
  startTime: '',
  duration: null,
  type: '',
  visitKind: '',
  status: 'scheduled',
  reasonForVisit: '',
  notes: '',
  treatmentId: '',
  cancelReason: '',
};

type TranslateFn = (...args: any[]) => string;

/** Align with Appointment mongoose maxlength. */
export const REASON_FOR_VISIT_MAX_LENGTH = 200;
export const CANCEL_REASON_MAX_LENGTH = 100;
export const NOTES_MAX_LENGTH = 500;

export const calculateEndTimeLabel = (
  startTime: string,
  durationMinutes: number | null
): string => {
  if (!startTime || !durationMinutes) return '';
  try {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    return format(addMinutes(startDate, durationMinutes), 'HH:mm');
  } catch {
    return '';
  }
};

export const buildAppointmentValidationSchema = (
  mode: AppointmentFormMode,
  t: TranslateFn
) => {
  const base = {
    subjectType: Yup.mixed<'patient' | 'lead'>()
      .oneOf(['patient', 'lead'])
      .required(),
    patientId: Yup.string().when('subjectType', {
      is: 'patient',
      then: (schema) => schema.required(t('validation.patientRequired')),
      otherwise: (schema) => schema,
    }),
    leadId: Yup.string().when('subjectType', {
      is: 'lead',
      then: (schema) => schema.required(t('validation.leadRequired')),
      otherwise: (schema) => schema,
    }),
    doctorId: Yup.string().required(t('validation.doctorRequired')),
    clinicId: Yup.string().required(t('validation.clinicRequired')),
    chairId: Yup.string(),
    date: Yup.date().nullable().required(t('validation.dateRequired')),
    startTime: Yup.string().required(t('validation.startTimeRequired')),
    duration: Yup.number()
      .nullable()
      .required(t('validation.durationRequired'))
      .min(MIN_APPOINTMENT_DURATION, t('validation.durationMin')),
    type: Yup.string().required(t('validation.typeRequired')),
    visitKind: Yup.string()
      .oneOf([...APPOINTMENT_VISIT_KINDS], t('validation.visitKindRequired'))
      .required(t('validation.visitKindRequired')),
    reasonForVisit: Yup.string().max(
      REASON_FOR_VISIT_MAX_LENGTH,
      t('validation.reasonMaxLength')
    ),
    notes: Yup.string().max(NOTES_MAX_LENGTH, t('validation.notesMaxLength')),
    treatmentId: Yup.string(),
  };

  const cancelReason = Yup.string().max(
    CANCEL_REASON_MAX_LENGTH,
    t('validation.cancelReasonMaxLength')
  );

  if (mode === 'create') {
    return Yup.object({
      ...base,
      status: Yup.string(),
      cancelReason,
    });
  }

  return Yup.object({
    ...base,
    status: Yup.string().required(t('validation.statusRequired')),
    cancelReason: cancelReason.when('status', {
      is: 'cancelled',
      then: (schema) =>
        schema.trim().required(t('validation.cancelReasonRequired')),
      otherwise: (schema) => schema,
    }),
  });
};

/**
 * Cancel reason is only required when transitioning into cancelled,
 * not when editing notes on an already-cancelled (terminal) appointment.
 */
export const buildEditValidationSchema = (
  savedStatus: string,
  t: TranslateFn
) => {
  const base = buildAppointmentValidationSchema('edit', t);
  if (savedStatus === 'cancelled' || isTerminalStatus(savedStatus)) {
    return base.shape({
      cancelReason: Yup.string().max(
        CANCEL_REASON_MAX_LENGTH,
        t('validation.cancelReasonMaxLength')
      ),
    });
  }
  return base;
};

const refId = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const record = value as { _id?: string; id?: string };
    return String(record._id || record.id || '');
  }
  return '';
};

export const mapAppointmentToFormValues = (
  appointment: AppointmentRecord,
  extractOrgDateParts: (
    value: Date | string | number | null | undefined
  ) => { date: string; time: string } | null
): AppointmentFormValues => {
  const orgParts = extractOrgDateParts(appointment.appointmentDate);
  const treatment = appointment.treatment;
  const patientId = refId(appointment.patient);
  const leadId = refId(appointment.lead);
  const subjectType: AppointmentSubjectType =
    patientId ? 'patient' : leadId ? 'lead' : 'patient';

  return {
    subjectType,
    patientId,
    leadId,
    doctorId: refId(appointment.dentist),
    clinicId: refId(appointment.clinic),
    chairId: getChairId(appointment.chair),
    date: orgParts ? parseOrgDateOnly(orgParts.date) : null,
    startTime: orgParts?.time ?? '',
    duration: appointment.duration ?? null,
    type: getAppointmentTypeId(appointment.appointmentType),
    visitKind: isAppointmentVisitKind(appointment.visitKind) ? appointment.visitKind : '',
    status: appointment.status || 'scheduled',
    reasonForVisit: appointment.reasonForVisit || '',
    notes: appointment.notes || '',
    treatmentId: refId(treatment),
    cancelReason: appointment.cancelReason || '',
  };
};

const toAppointmentDate = (
  values: AppointmentFormValues,
  zonedDateTimeToUtc: (datePart: string, timePart: string) => Date
): Date => {
  const formattedDate = format(values.date!, 'yyyy-MM-dd');
  return zonedDateTimeToUtc(formattedDate, values.startTime);
};

export const buildCreateAppointmentPayload = (
  values: AppointmentFormValues,
  zonedDateTimeToUtc: (datePart: string, timePart: string) => Date
): CreateAppointmentPayload => {
  const appointmentDate = toAppointmentDate(values, zonedDateTimeToUtc);
  const endTime = new Date(appointmentDate);
  endTime.setMinutes(endTime.getMinutes() + (values.duration ?? 0));

  return {
    ...(values.subjectType === 'patient'
      ? { patient: values.patientId }
      : { lead: values.leadId }),
    clinic: values.clinicId,
    dentist: values.doctorId,
    appointmentDate,
    endTime,
    duration: values.duration!,
    appointmentType: values.type,
    visitKind: values.visitKind as AppointmentVisitKind,
    reasonForVisit: values.reasonForVisit || '',
    notes: values.notes || '',
    status: values.status === 'confirmed' ? 'confirmed' : 'scheduled',
    ...(values.chairId ? { chair: values.chairId } : {}),
    ...(values.treatmentId ? { treatment: values.treatmentId } : {}),
  };
};

const sameInstant = (a: Date | string | undefined, b: Date | string | undefined): boolean => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return new Date(a).getTime() === new Date(b).getTime();
};

/**
 * Diff-based update payload so unchanged schedule fields are omitted
 * (avoids re-running availability checks / auto-reschedule on notes-only edits).
 */
export const buildUpdateAppointmentPayload = (
  values: AppointmentFormValues,
  baseline: AppointmentFormValues,
  zonedDateTimeToUtc: (datePart: string, timePart: string) => Date
): UpdateAppointmentPayload => {
  const patch: UpdateAppointmentPayload = {};

  if (
    values.subjectType !== baseline.subjectType ||
    values.patientId !== baseline.patientId ||
    values.leadId !== baseline.leadId
  ) {
    if (values.subjectType === 'patient') {
      patch.patient = values.patientId;
    } else {
      patch.lead = values.leadId;
    }
  }
  if (values.clinicId !== baseline.clinicId) {
    patch.clinic = values.clinicId;
  }
  if (values.doctorId !== baseline.doctorId) {
    patch.dentist = values.doctorId;
  }
  if (values.duration !== baseline.duration) {
    patch.duration = values.duration ?? undefined;
  }
  if (values.type !== baseline.type) {
    patch.appointmentType = values.type;
  }
  if (values.visitKind !== baseline.visitKind && isAppointmentVisitKind(values.visitKind)) {
    patch.visitKind = values.visitKind;
  }
  if (values.reasonForVisit !== baseline.reasonForVisit) {
    patch.reasonForVisit = values.reasonForVisit || '';
  }
  if (values.notes !== baseline.notes) {
    patch.notes = values.notes || '';
  }
  if (values.status !== baseline.status) {
    patch.status = values.status;
  }

  const nextDate =
    values.date && values.startTime
      ? toAppointmentDate(values, zonedDateTimeToUtc)
      : undefined;
  const baselineDate =
    baseline.date && baseline.startTime
      ? toAppointmentDate(baseline, zonedDateTimeToUtc)
      : undefined;
  if (!sameInstant(nextDate, baselineDate) && nextDate) {
    patch.appointmentDate = nextDate;
  }

  if (values.chairId !== baseline.chairId) {
    patch.chair = values.chairId || null;
  }

  if (values.treatmentId !== baseline.treatmentId) {
    patch.treatment = values.treatmentId || null;
  }

  if (values.status === 'cancelled' && values.cancelReason.trim()) {
    patch.cancelReason = values.cancelReason.trim();
  }

  return patch;
};

export type RequireChairMode = 'if_configured' | 'always' | 'never';

export const validateRequiredChair = (
  values: AppointmentFormValues,
  options: {
    requireChair: RequireChairMode;
    activeChairCount: number;
  },
  t: TranslateFn
): string | null => {
  const { requireChair, activeChairCount } = options;
  const needed =
    requireChair === 'always' ||
    (requireChair === 'if_configured' && activeChairCount > 0);

  if (needed && !values.chairId) {
    return t('validation.chairRequired');
  }
  return null;
};

const sameDateValue = (a: Date | null, b: Date | null): boolean => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.getTime() === b.getTime();
};

/**
 * Dirty check vs a captured baseline (after create prefills / edit hydrate).
 * Pass live text-field drafts so keystrokes count before Formik blur commit.
 */
export const isAppointmentFormDirty = (
  values: AppointmentFormValues,
  baseline: AppointmentFormValues,
  textDraft?: { reasonForVisit: string; notes: string }
): boolean => {
  const reason = textDraft?.reasonForVisit ?? values.reasonForVisit;
  const notes = textDraft?.notes ?? values.notes;

  return (
    values.subjectType !== baseline.subjectType ||
    values.patientId !== baseline.patientId ||
    values.leadId !== baseline.leadId ||
    values.doctorId !== baseline.doctorId ||
    values.clinicId !== baseline.clinicId ||
    values.chairId !== baseline.chairId ||
    !sameDateValue(values.date, baseline.date) ||
    values.startTime !== baseline.startTime ||
    values.duration !== baseline.duration ||
    values.type !== baseline.type ||
    values.visitKind !== baseline.visitKind ||
    values.status !== baseline.status ||
    reason !== baseline.reasonForVisit ||
    notes !== baseline.notes ||
    values.treatmentId !== baseline.treatmentId ||
    values.cancelReason !== baseline.cancelReason
  );
};
